import React, { useEffect, useState } from 'react';
import { courtIntelligenceApi } from '../api/courtIntelligenceApi';
import type { CourtIntelligencePreview as PreviewData, CourtLifecycleStatus } from '../types/courtIntelligence.types';

/**
 * Court Intelligence Preview panel for the MatterCreateForm (Engagement Details section).
 * Shows practice-area-filtered court data (forms, judges, fees) for a given
 * jurisdiction + practice area. When a case subtype is selected, shows a split
 * view with the subtype workflow (phases/tasks) and subtype-specific forms.
 */

interface CourtIntelligencePreviewProps {
  jurisdictionCode: string;
  practiceArea: string;
  stateCode?: string;
  caseSubtype?: string;
}

function LifecycleBadge({ status }: { status: CourtLifecycleStatus }) {
  const config: Record<CourtLifecycleStatus, { bg: string; text: string; label: string }> = {
    validated: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300', label: 'Validated' },
    draft: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-300', label: 'Draft' },
    stale: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-300', label: 'Stale' },
  };
  const c = config[status] || config.draft;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
}

function StatCard({ label, value, subtitle }: { label: string; value: number | string; subtitle?: string }) {
  return (
    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
      <div className="text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</div>
      {subtitle && <div className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">{subtitle}</div>}
    </div>
  );
}

const PRIORITY_COLORS: Record<string, string> = {
  urgent: 'text-red-600 dark:text-red-400',
  high: 'text-orange-600 dark:text-orange-400',
  medium: 'text-yellow-600 dark:text-yellow-400',
  low: 'text-gray-500 dark:text-gray-400',
};

const CourtIntelligencePreview: React.FC<CourtIntelligencePreviewProps> = ({
  jurisdictionCode,
  practiceArea,
  stateCode,
  caseSubtype,
}) => {
  const [data, setData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [showAllForms, setShowAllForms] = useState(false);

  useEffect(() => {
    if (!jurisdictionCode || !practiceArea) {
      setData(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    courtIntelligenceApi
      .getPreview({
        jurisdiction_code: jurisdictionCode,
        practice_area: practiceArea,
        state_code: stateCode,
        case_subtype: caseSubtype,
      })
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('[CourtIntelligencePreview] API not available:', err?.message);
          setError('Court intelligence is being set up for this jurisdiction.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [jurisdictionCode, practiceArea, stateCode, caseSubtype]);

  if (!jurisdictionCode || !practiceArea) {
    return null;
  }

  const sub = data?.subtype;
  // Letter-for-letter fidelity: sub.label comes from the resolver and
  // already prefers the jurisdiction's local term. If it's missing, tidy
  // the raw canonical key (underscores → spaces) rather than showing it
  // as a snake_case slug.
  const subtypeDisplay = sub?.label || (caseSubtype ? caseSubtype.replace(/_/g, ' ') : '');
  const headerLabel = sub
    ? `${subtypeDisplay} - ${sub.forms_total} forms`
    : `${data?.forms.total || 0} ${practiceArea} forms`;

  return (
    <div className="mt-2 border border-blue-200 dark:border-blue-800 rounded-lg overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L10 6.022V16h3a1 1 0 110 2H7a1 1 0 110-2h3V6.022L6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.789l1.599.799L9 4.323V3a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          <span className="font-medium text-blue-800 dark:text-blue-300 text-sm">Court Intelligence</span>
          {data && <LifecycleBadge status={data.lifecycle_status} />}
          {data && (
            <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {headerLabel} · {data.judges.total} judges · {data.fees.total} fees
            </span>
          )}
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400 transition-transform ${collapsed ? '' : 'rotate-180'}`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>

      {/* Body */}
      {!collapsed && (
        <div className="px-4 py-3 bg-white dark:bg-gray-800">
          {/* Loading */}
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-3 justify-center">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Loading court intelligence...
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          )}

          {/* Data */}
          {!loading && !error && data && (
            <div className="space-y-3">
              {/* Court name */}
              {data.court_name && (
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {data.court_name}
                </div>
              )}

              {/* ═══ SUBTYPE VIEW: Split workflow + forms ═══ */}
              {sub ? (
                <>
                  {/* Stats row — subtype-specific */}
                  <div className="grid grid-cols-3 gap-3">
                    <StatCard
                      label="Required Forms"
                      value={sub.forms_total}
                      subtitle={sub.forms_fillable > 0 ? `${sub.forms_fillable} fillable` : undefined}
                    />
                    <StatCard label="Judges" value={data.judges.total} />
                    <StatCard label="Fee Items" value={data.fees.total} />
                  </div>

                  {/* Split view: Workflow | Forms */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Left: Workflow phases */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                          Workflow Preview
                        </span>
                      </div>
                      <div className="p-3 space-y-2.5 max-h-64 overflow-y-auto">
                        {sub.phases.length > 0 ? (
                          sub.phases
                            .sort((a, b) => a.order - b.order)
                            .map((phase) => (
                              <div key={phase.order}>
                                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                  Phase {phase.order}: {phase.name}
                                </div>
                                <div className="space-y-0.5 pl-3 border-l-2 border-blue-200 dark:border-blue-800">
                                  {phase.tasks.map((task, ti) => (
                                    <div key={ti} className="flex items-start gap-1.5 text-xs">
                                      <span className={`mt-0.5 ${task.is_court_mandated ? 'text-blue-500' : 'text-gray-400'}`}>
                                        {task.is_court_mandated ? '●' : '○'}
                                      </span>
                                      <span className="text-gray-700 dark:text-gray-300 leading-tight">
                                        {task.title}
                                      </span>
                                      {task.priority && task.priority !== 'medium' && (
                                        <span className={`flex-shrink-0 ${PRIORITY_COLORS[task.priority] || ''}`}>
                                          {task.priority === 'high' || task.priority === 'urgent' ? '!' : ''}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))
                        ) : (
                          <p className="text-xs text-gray-400 italic">No workflow phases defined</p>
                        )}
                        {sub.phases.length > 0 && (
                          <div className="text-[10px] text-gray-400 dark:text-gray-500 pt-1 border-t border-gray-100 dark:border-gray-700">
                            {sub.phases.length} phases · {sub.phases.reduce((sum, p) => sum + p.tasks.length, 0)} tasks
                            · ● = court-mandated
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right: Forms list */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                          Forms ({sub.forms_total} of {data.forms.total})
                        </span>
                      </div>
                      <div className="p-3 max-h-64 overflow-y-auto">
                        <div className="flex flex-wrap gap-1.5">
                          {sub.forms_sample.map((f) => (
                            <span
                              key={f.form_number}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                                f.field_count > 0
                                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                              }`}
                              title={`${f.form_name}${f.field_count > 0 ? ` (${f.field_count} fillable fields)` : ''}`}
                            >
                              <span className="font-medium">{f.form_number}</span>
                              {f.field_count > 0 && (
                                <span className="text-[10px] opacity-70">{f.field_count}f</span>
                              )}
                            </span>
                          ))}
                        </div>

                        {/* Toggle to show all practice area forms */}
                        {data.forms.total > sub.forms_total && (
                          <button
                            type="button"
                            onClick={() => setShowAllForms(v => !v)}
                            className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            {showAllForms
                              ? `Hide other ${practiceArea} forms`
                              : `View all ${data.forms.total} ${practiceArea} forms`}
                          </button>
                        )}

                        {showAllForms && data.forms.sample.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                            <div className="text-[10px] text-gray-400 dark:text-gray-500 mb-1">
                              Other {practiceArea} forms:
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {data.forms.sample
                                .filter(f => !sub.forms_sample.some(sf => sf.form_number === f.form_number))
                                .map((f) => (
                                  <span
                                    key={f.form_number}
                                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                                    title={f.form_name}
                                  >
                                    {f.form_number}
                                  </span>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                /* ═══ DEFAULT VIEW: Practice area overview (no subtype) ═══ */
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <StatCard
                      label={`${practiceArea} Forms`}
                      value={data.forms.total}
                      subtitle={data.forms.fillable_count > 0 ? `${data.forms.fillable_count} fillable` : undefined}
                    />
                    <StatCard label="Judges" value={data.judges.total} />
                    <StatCard label="Fee Items" value={data.fees.total} />
                  </div>

                  {data.forms.sample.length > 0 && (
                    <div className="space-y-1">
                      <div className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        Key forms for {practiceArea}:
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {data.forms.sample.map((f) => (
                          <span
                            key={f.form_number}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                              f.field_count > 0
                                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                            }`}
                            title={`${f.form_name}${f.field_count > 0 ? ` (${f.field_count} fillable fields)` : ''}`}
                          >
                            <span className="font-medium">{f.form_number}</span>
                            {f.field_count > 0 && (
                              <span className="text-[10px] opacity-70">{f.field_count}f</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CourtIntelligencePreview;
